import type { ComponentProps } from 'react';
import React, { useMemo } from 'react';
import { useSegments } from 'expo-router';
import { Platform, StyleSheet } from 'react-native';
import { Easing, useAnimatedStyle, useDerivedValue, withTiming } from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useStyle } from 'react-native-style-utilities';

import type { NoticeBannersStack } from '@sg/garnish';
import {
  NOTICE_BANNER_PADDING,
  theme,
  useModalContext,
  useNavbarContainerHeight,
  useResponsive,
} from '@sg/garnish';

import { useFeatureFlag } from '@order/LaunchDarkly';
import { useUsableNetBannerOffset } from '@order/usable-net';

import { checkIfPresentedAsModalWithoutHeader } from '../../navigation';
import { useActiveScreenOptions } from '../../routing/hooks/useActiveScreenOptions';
import { NoticeBannersOverlayStack } from './components';

// ────────────────────────────────────────────────────────────────────────────────

export const NoticeBannersOverlay = () => {
  const safeAreaInsets = useSafeAreaInsets();
  const modalContext = useModalContext();
  const segments = useSegments();
  const { currentBreakpoint } = useResponsive();

  const { height: navbarV2Height, offsetStyle: navbarV2OffsetStyle } = useNavbarContainerHeight();

  const activeScreenOptions = useActiveScreenOptions();
  const isToastEnabled = useFeatureFlag('CELS-6140-order-toast-notifications-enabled');

  const isPresentedFromBottom = currentBreakpoint.isXS || isToastEnabled;
  const isInTabs = segments[0] === TABS_SEGMENT;
  const hasVisibleTopNavbar = isInTabs || activeScreenOptions?.headerShown !== false;
  const navbarLayout = activeScreenOptions?.navbarLayout ?? 'floating';
  const navbarHeight = navbarV2Height;
  const shouldAddNavbarGap = !hasVisibleTopNavbar || navbarLayout !== 'floating';
  const navbarTopOffset =
    (hasVisibleTopNavbar ? navbarHeight : 0) + (shouldAddNavbarGap ? NAVBAR_V2_BANNER_GAP : 0);

  const isPresenetedAsModalWithoutHeader =
    checkIfPresentedAsModalWithoutHeader(activeScreenOptions);

  // To identify whether the active screen is presented as a `Modal`
  // or whether a `Modal` component (garnish) is open.
  const isFullScreenModal =
    isPresenetedAsModalWithoutHeader || modalContext?.hasActiveModal === true;

  const hasVisibleBottomNavbar = currentBreakpoint.isXS && isInTabs && !isFullScreenModal;
  const shouldUseBottomNavbarTreatment = hasVisibleBottomNavbar;

  // ─── Offset & Animation ──────────────────────────────────────────

  const usableNetBannerOffset = useUsableNetBannerOffset();
  const noticeBannerOffset = useDerivedValue(
    () => withTiming(navbarTopOffset + usableNetBannerOffset, ANIMATION),
    [navbarTopOffset, usableNetBannerOffset],
  );

  const animatedStyle = useAnimatedStyle(() => ({
    transform: [{ translateY: noticeBannerOffset.value }],
  }));

  const minBottomGap = currentBreakpoint.isXS ? NAVBAR_V2_BANNER_GAP : theme.spacing['14'];

  const bottomGapStyle = useStyle(
    () => ({
      bottom: Math.max(minBottomGap, safeAreaInsets.bottom + theme.spacing['2']),
    }),
    [minBottomGap, safeAreaInsets.bottom],
  );

  const bottomOffsetStyle = shouldUseBottomNavbarTreatment ? navbarV2OffsetStyle : bottomGapStyle;
  const offsetStyle = isPresentedFromBottom ? bottomOffsetStyle : styles.topOffset;
  const shouldAnimateOffset = !isPresentedFromBottom;
  const offsetAnimation = shouldAnimateOffset ? animatedStyle : undefined;

  // ─────────────────────────────────────────────────────────────────

  const bannerPadding = useMemo<BannerPaddings>(() => {
    const safeAreaTopInset = safeAreaInsets.top;

    if (isPresentedFromBottom) {
      return { ...NOTICE_BANNER_PADDING };
    }

    return {
      ...NOTICE_BANNER_PADDING,
      top: Math.max(NOTICE_BANNER_PADDING.top, safeAreaTopInset + theme.spacing['2']),
    };
  }, [isPresentedFromBottom, safeAreaInsets.top]);

  // ─────────────────────────────────────────────────────────────────

  return (
    <NoticeBannersOverlayStack
      bannerPadding={bannerPadding}
      bannerVariation="floating"
      pointerEvents="box-none"
      style={[styles.stack, StyleSheet.absoluteFill, offsetStyle, offsetAnimation]}
    />
  );
};

// ────────────────────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  topOffset: {
    top: 0,
  },
  stack: {
    zIndex: Platform.select({
      default: theme.zIndex.overModal,
      ios: undefined, // On iOS, we use the `FullWindowOverlay ` component which conflicts with `zIndex`.
    }),
  },
});

// ─── Constants ───────────────────────────────────────────────────────────────

const ANIMATION = { duration: 500, easing: Easing.out(Easing.exp) };
const NAVBAR_V2_BANNER_GAP = theme.spacing['4'];
const TABS_SEGMENT = '(tabs)';

// ─── Types ───────────────────────────────────────────────────────────────────

type BannerPaddings = ComponentProps<typeof NoticeBannersStack>['bannerPadding'];
